import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { parseDateToLgComun4 } from "../../../helpers/dateTransform";
import { MarkAllAsRead, NotificationByUserPost } from "../../../apis";
import {
  Box,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useReportContext } from "../../Report/ReportContext/useReportContext";
import { AvatarLetter } from "../../MainComponents/Avatar/AvatarLetter";

const Notification = ({
  reportsData,
  filterType = "all",
  onClose,
  rechargeData,
}) => {
  // CustomHooks
  const [t] = useTranslation("components");
  const navigate = useNavigate();
  const { status_locales } = useReportContext();
  // Initial state
  const [notifications, setNotifications] = useState([]);
  const [refresh, setRefresh] = useState(false);
  // Returns the number of days from the given date to the present day
  const calculateOpenDays = (created_at) => {
    const createdDate = dayjs(created_at);
    const today = dayjs();
    return today.diff(createdDate, "day");
  };

  const statusTranslations = {
    Borrador: "draft",
    Nuevo: "new",
    Pendiente: "pending",
    "En proceso": "process",
    Finalizado: "finish",
    Cerrado: "closed",
  };
  // Mark all notifications as read
  const handleMarkAllAsRead = async () => {
    try {
      await MarkAllAsRead().then(() => rechargeData());
      // Refetch notifications
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  // Data structure simplified
  const updateReportsData = useCallback(() => {
    try {
      if (!reportsData) return;

      const register = reportsData
        .filter((report) => {
          if (filterType === "unread") return report.deleted_at === null;
          if (filterType === "read") return report.deleted_at !== null;
          return true; // show ALL notifications
        })
        .map((reportItem) => {
          const info = reportItem?.data?.info || [];

          const translatedStatus =
            statusTranslations[info[1]?.value] || "draft";
          const color =
            status_locales[translatedStatus]?.color ||
            status_locales["draft"].color;

          const infoList = reportItem?.data?.info || [];
          const folioItem = infoList.find(item => item.name === "Folio");



          const card = {
            id: reportItem.id,
            title: reportItem?.data?.title || "",
            message: reportItem?.data?.message || "",
            report_id: reportItem?.metadata?.report_id || "",
            created_at: reportItem?.created_at || "",
            folio: folioItem ? folioItem.value : "",
            name: reportItem?.metadata?.user_name || t("Navbar.anonymous"),
            open_days: reportItem?.created_at
              ? `${t("NotificationCards.open_since")}: ${calculateOpenDays(
                  reportItem.created_at
                )} ${
                  calculateOpenDays(reportItem.created_at) > 1
                    ? t("NotificationCards.days")
                    : t("NotificationCards.days")
                }`
              : "",
            deleted_at: reportItem?.deleted_at || "",
            primary_category: info[0]?.name || "",
            primary_value: info[0]?.value || "",
            secondary_category: info[1]?.name || "",
            secondary_value: info[1]?.value || "",
            color: color,
          };

          return card;
        });

      setNotifications(register);
    } catch (error) {
      console.error("Error processing reports data:", error);
    }
  }, [reportsData, filterType]);

  useEffect(() => {
    updateReportsData();
  }, [reportsData, refresh, updateReportsData]);

  const handleReportClick = useCallback(
    (reportId, id) => {
      if (Number.isInteger(reportId)) {
        navigate(`/reports/${reportId}`);
        onClose?.();
      }
      NotificationByUserPost(id);
      setRefresh((prev) => !prev);
    },
    [navigate, onClose]
  );

  return (
    <Box
      className="mt-20 dashboard-card p-2 dyBorder1"
      id="recent-reports"
      sx={{ height: "75vh", overflowY: "auto" }}
    >
      {notifications.length > 0 ? (
        <>
          <Typography variant="h6" textAlign="center">
            {`${t("NotificationCards.last_notifications")}:`}
          </Typography>
          {filterType === "unread" && (
            <Stack display="flex" alignItems="end" mr={1}>
              <Link onClick={handleMarkAllAsRead} underline="hover">
                {t("Navbar.mark_all_as_read")}
              </Link>
            </Stack>
          )}
          <List dense>
            {notifications.map((notification) => (
              <NotificationItem 
                key={notification.id} 
                notification={notification} 
                handleReportClick={handleReportClick}
              />
            ))}
          </List>
        </>
      ) : (
        <Typography variant="h6" textAlign="center">
          {`${t("NotificationCards.no_notifications")}...`}
        </Typography>
      )}
    </Box>
  );
};

const NotificationItem = ({ notification, handleReportClick }) => {
  const theme = useTheme();
  return (
    <ListItem
      key={notification.id}
      onClick={() =>
        handleReportClick(notification?.report_id, notification?.id)
      }
      sx={{
        border: `1px solid ${theme.palette.blueGray50.main}`,
        borderLeft: `8px solid ${notification.color}`,
        my: 1,
        cursor: "pointer",
      }}
      className="rounded"
    >
      {/* <ListItemAvatar sx={{ scale: 1.75, p: 1, mr: "-10px" }}>
        <AvatarLetter contact={notification} />
      </ListItemAvatar> */}
      <ListItemText
        primary={notification.title}
        primaryTypographyProps={{
          component: "span",
          fontSize: 15,
          fontWeight: 600,
        }}
        secondary={
          <Typography component="span">
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                flexFlow: "row wrap",
              }}
            >
              <Typography component="span" variant="body2">
                {notification.message} <br/>
                <strong>Folio: {notification.folio}</strong>
              </Typography>
             
              <Typography
                component="span"
                fontStyle="italic"
                fontSize={12}
              >
                {parseDateToLgComun4(notification.created_at)}
              </Typography>
            </Stack>
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                flexFlow: "row wrap",
                mt: 0.5,
              }}
            >
              <Box></Box>
              <Typography
                component="span"
                variant="body2"
                fontSize={12}
              >
                {notification.open_days}
              </Typography>
            </Stack>
          </Typography>
        }
      />
    </ListItem>
  )
}
export default Notification;
